import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export function Navbar({ onApply }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const LINKS = [
    { label: t("nav.program"), href: "#program" },
    { label: t("product.overline"), href: "#prodotto" },
    { label: t("nav.why"), href: "#why" },
    { label: t("nav.how"), href: "#how" },
    { label: t("tiers.overline"), href: "#livelli" },
    { label: t("nav.faq"), href: "#faq" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goDash = () => navigate(user?.role === "admin" ? "/admin" : "/creator");

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-2xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <img src={LOGO_URL} alt="AURA+" className="h-9 md:h-11 w-auto mix-blend-screen opacity-95 group-hover:opacity-100 transition-opacity" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-sm text-zinc-400 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          {user ? (
            <button data-testid="nav-dashboard-btn" onClick={goDash} className="text-sm text-zinc-300 hover:text-white transition-colors">
              {t("nav.myArea")}
            </button>
          ) : (
            <Link to="/login" data-testid="nav-login-link" className="text-sm text-zinc-400 hover:text-white transition-colors duration-300">
              {t("nav.login")}
            </Link>
          )}
          <button
            data-testid="nav-apply-btn"
            onClick={onApply}
            className="rounded-full bg-white text-black text-sm font-semibold px-5 py-2.5 hover:bg-zinc-200 active:scale-95 transition-[background-color,transform] duration-300"
          >
            {t("nav.apply")}
          </button>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen((v) => !v)} className="text-white p-2">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-black/90 backdrop-blur-2xl border-b border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-base text-zinc-300 hover:text-white">
                  {l.label}
                </a>
              ))}
              <div className="h-px bg-white/10 my-1" />
              {user ? (
                <button onClick={() => { setOpen(false); goDash(); }} className="text-start text-base text-zinc-300">{t("nav.myArea")}</button>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="text-base text-zinc-300">{t("nav.login")}</Link>
              )}
              <button onClick={() => { setOpen(false); onApply?.(); }} className="rounded-full bg-white text-black text-sm font-semibold px-5 py-3 mt-1">
                {t("nav.apply")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
